import type { AgentSummary } from '@ralph/contracts'
import { AGENT_ID_PATTERN } from '@ralph/contracts'
import clsx from 'clsx'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Badge, Button, Input, Textarea } from '@/components/ui/primitives.tsx'
import { useSaveAgentLayer } from '@/hooks/use-agents.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'

/**
 * The agent rail.
 *
 * Creating an agent asks for a prompt in the same breath as its name, because an agent
 * that resolves is minimally a prompt: an id-only create would produce a row that cannot
 * run, and the operator would meet that as a resolution error later instead of as a field
 * in front of them now.
 *
 * The selected row is the only one that is drawn at all — a solid card with a hairline and
 * the panel shadow, exactly as the shell's rail draws the open conversation. Everything
 * else is muted text on nothing, so a list of thirty agents reads as one lit row rather
 * than as thirty competing buttons.
 */

type AgentListProps = {
  items: AgentSummary[]
  selectedId: string | null
  onSelect: (agentId: string) => void
  loading: boolean
  error: Error | null
  canEdit: boolean
}

export const AgentList = ({ items, selectedId, onSelect, loading, error, canEdit }: AgentListProps) => {
  // In the body rather than at module scope, so a locale switch re-renders these timestamps
  // — and every word beside them — instead of leaving them in the language they were first
  // drawn in (PRD-27 F-3, LOC-012).
  const { formatAgo, t: messages } = useI18n()
  const t = messages.admin.studio.list
  const [creating, setCreating] = useState(false)

  return (
    // Below `lg` the rail is a full-width band above the editor with its own scroll, capped
    // so it can never push the thing it selects off the screen.
    <div
      className={clsx(
        'flex max-h-[38dvh] shrink-0 flex-col border-b border-rule bg-card',
        'lg:max-h-none lg:w-[290px] lg:border-r lg:border-b-0'
      )}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-rule px-4 py-3">
        <span className="eyebrow">{t.title}</span>
        {canEdit && !creating && (
          <Button variant="ghost" size="sm" icon={<Plus size={13} />} onClick={() => setCreating(true)}>
            {t.new}
          </Button>
        )}
      </header>

      {creating && (
        <NewAgentForm
          existingIds={items.map((item) => item.agentId)}
          onCancel={() => setCreating(false)}
          onCreated={(agentId) => {
            setCreating(false)
            onSelect(agentId)
          }}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {loading && <p className="px-2 py-3 text-[13px] text-fg-subtle">{t.loading}</p>}

        {/* The server's English sentence used to be rendered straight in here, whatever it
            said. `describeApiError` looks the refusal up by its key instead, and keeps that
            prose only as the fallback for a key this build has not learned (PRD-27 LOC-033). */}
        {error && (
          <p className="px-2 py-3 text-[12.5px] leading-relaxed text-alert">{describeApiError(messages, error)}</p>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="px-2 py-3 text-[12.5px] leading-relaxed text-fg-subtle">{t.empty}</p>
        )}

        <ul className="space-y-0.5">
          {items.map((agent) => {
            const active = selectedId === agent.agentId
            return (
              <li key={agent.agentId}>
                <button
                  type="button"
                  onClick={() => onSelect(agent.agentId)}
                  className={clsx(
                    'w-full rounded-chip border px-2.5 py-2 text-left',
                    'transition-[background-color,border-color,color] duration-200',
                    active
                      ? 'border-rule bg-card-solid text-fg'
                      : 'border-transparent text-fg-muted hover:bg-card-solid hover:text-fg'
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className={clsx('truncate text-[13px]', active ? 'font-semibold' : 'font-medium')}>
                      {agent.displayName || agent.agentId}
                    </span>
                    {!agent.enabled && <Badge>{t.off}</Badge>}
                  </span>
                  <span className="readout mt-0.5 block truncate text-fg-subtle">{agent.agentId}</span>
                  <span className="readout mt-0.5 block text-fg-subtle">
                    {t.rowMeta(agent.version, formatAgo(agent.updatedAt))}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

const NewAgentForm = ({
  existingIds,
  onCancel,
  onCreated
}: {
  existingIds: string[]
  onCancel: () => void
  onCreated: (agentId: string) => void
}) => {
  // The whole catalogue rather than just this form's corner of it: the create failure is
  // rendered through `describeApiError`, which needs every error sentence to look one up.
  const messages = useT()
  const t = messages.admin.studio.list.create
  const create = useSaveAgentLayer()
  const [agentId, setAgentId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')

  const trimmed = agentId.trim()
  const idProblem =
    trimmed === ''
      ? null
      : existingIds.includes(trimmed)
        ? t.idTaken
        : AGENT_ID_PATTERN.test(trimmed)
          ? null
          : t.idPattern

  const submit = () => {
    if (idProblem !== null || trimmed === '' || systemPrompt.trim() === '') return
    create.mutate(
      {
        agentId: trimmed,
        body: { systemPrompt: systemPrompt.trim() },
        displayName: displayName.trim() === '' ? trimmed : displayName.trim(),
        changeNote: t.note
      },
      { onSuccess: () => onCreated(trimmed) }
    )
  }

  return (
    <form
      className="shrink-0 space-y-2.5 border-b border-rule bg-card-solid px-3 py-3"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div>
        <label htmlFor="new-agent-id" className="eyebrow mb-1 block">
          {t.idLabel}
        </label>
        <Input
          id="new-agent-id"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          placeholder={t.idPlaceholder}
          autoFocus
          className="readout"
        />
        {idProblem && <p className="readout mt-1 text-alert">{idProblem}</p>}
      </div>

      <div>
        <label htmlFor="new-agent-name" className="eyebrow mb-1 block">
          {t.nameLabel}
        </label>
        <Input
          id="new-agent-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t.namePlaceholder}
        />
      </div>

      <div>
        <label htmlFor="new-agent-prompt" className="eyebrow mb-1 block">
          {t.promptLabel}
        </label>
        <Textarea
          id="new-agent-prompt"
          rows={5}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder={t.promptPlaceholder}
          className="font-mono text-[12.5px] leading-relaxed"
        />
      </div>

      <div className="flex gap-2 pt-0.5">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={create.isPending || trimmed === '' || idProblem !== null || systemPrompt.trim() === ''}
        >
          {t.submit}
        </Button>
        <Button size="sm" onClick={onCancel}>
          {t.cancel}
        </Button>
      </div>

      {create.isError && <p className="text-[12.5px] text-alert">{describeApiError(messages, create.error)}</p>}
    </form>
  )
}
